<script setup>
/**
 * One template in the gallery grid: a miniature widget-bubble rendering of
 * the template content (through the same normalize helper the builder uses)
 * — hero image, headline, body and CTA chips — with the template name
 * beneath it. The whole card is the pick button.
 */
import { computed } from 'vue';
import { getContrastingTextColor } from '@chatwoot/utils';
import { resolvePresetGradient } from 'shared/helpers/campaignImagePresets';

import {
  DEFAULT_THEME_COLOR,
  normalizeCampaignContent,
} from 'dashboard/components-next/Campaigns/helpers/campaignContent';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const content = computed(() =>
  normalizeCampaignContent({ content: props.template.content })
);

const presetGradient = computed(() =>
  resolvePresetGradient(content.value.imageUrl)
);

const primaryCtaStyle = computed(() => {
  const color = content.value.themeColor || DEFAULT_THEME_COLOR;
  return { backgroundColor: color, color: getContrastingTextColor(color) };
});
</script>

<template>
  <button
    type="button"
    :disabled="disabled"
    class="flex flex-col overflow-hidden text-left transition-shadow rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container hover:shadow-md focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-60 disabled:pointer-events-none"
    @click="emit('select')"
  >
    <div class="w-full px-4 pt-4 pb-3 bg-n-alpha-1" aria-hidden="true">
      <div
        class="flex flex-col overflow-hidden shadow-sm rounded-xl rounded-bl-sm bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-container"
      >
        <div
          v-if="presetGradient"
          class="w-full h-16"
          :style="{ backgroundImage: presetGradient }"
        />
        <img
          v-else-if="content.imageUrl"
          :src="content.imageUrl"
          alt=""
          loading="lazy"
          class="object-cover w-full h-16"
        />
        <div class="flex flex-col gap-1 p-3">
          <span class="text-sm font-semibold text-n-slate-12 line-clamp-1">
            {{ content.headline }}
          </span>
          <p
            v-if="content.body"
            class="mb-0 text-xs text-n-slate-11 line-clamp-2"
          >
            {{ content.body }}
          </p>
          <div v-if="content.ctas.length" class="flex flex-wrap gap-1 mt-1.5">
            <span
              v-for="(cta, index) in content.ctas"
              :key="index"
              class="inline-flex items-center h-6 px-2.5 font-medium rounded-full text-label-small"
              :class="index === 0 ? '' : 'bg-n-alpha-2 text-n-slate-12'"
              :style="index === 0 ? primaryCtaStyle : undefined"
            >
              {{ cta.label }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <span class="px-4 py-3 text-sm font-medium text-n-slate-12 line-clamp-1">
      {{ template.name }}
    </span>
  </button>
</template>
